import { Controller } from '@hotwired/stimulus'
import Choices from 'choices.js'

let importCSS

/*
 * This controller allows multi-select form fields to use
 * Stimulus and the choices-js package instead of JQuery.
 *
 * To add a placeholder, set the 'data-placeholder' attribute
 * of your HTML element and make sure 'include_blank' is not
 * set to true.
 */
export default class extends Controller {
  static targets = ['association']
  static values = {
    choices: Array,
    disable: Boolean,
    preserveOrder: Boolean,
    reset: Boolean,
    configOverrides: { type: Object, default: {} },
  }

  initialize() {
    importCSS ??= import('@/styles/_choices.scss')
  }

  async connect() {
    await importCSS
    let associationTarget = this.hasAssociationTarget
      ? this.associationTarget
      : this.element
    this.multiSelect = new Choices(associationTarget, {
      ...this.defaultConfig(),
      callbackOnCreateTemplates: this.preserveOrderValue
        ? () => ({
            item(...args) {
              let item = Choices.defaults.templates.item.call(this, ...args)
              // <input name="email_template_block[reference_posts][]" value="1">
              const input = document.createElement('input')
              input.type = 'hidden'
              input.name = this.passedElement.element.name
              input.value = args[1].value
              item.appendChild(input)
              return item
            },
          })
        : null,
      ...this.configOverridesValue,
    })
    if (this.preserveOrderValue) associationTarget.disabled = true

    if (this.hasChoicesValue) {
      this.multiSelect.setChoices(this.choicesValue, 'value', 'label', true)
    }

    if (this.resetValue) {
      associationTarget.value = ''
    }

    if (this.disableValue == true) {
      this.disable()
    }

    // This allows fancy access to the controller from outside the scope of
    // the controller. Using built-in actions is only available if the two
    // controllers are at the same level (scope). This technique doesn't work
    // if you reuse this controller, say if you have two choices on the
    // same form. And if you want the form to be able to programmatically change
    // selections, this line allows you to connect to the specific instance
    // you need. See the user_criteria controller for examples on how this
    // is done.
    //
    // document.querySelector('#your-id').choices.clear()
    //
    // It breaks scoping, but sometimes you have to do this.
    this.element[this.identifier] = this
  }

  defaultConfig() {
    return {
      removeItemButton: true,
      addItems: true,
      position: 'bottom',
      allowHTML: true,
      shouldSort: false,
      searchResultLimit: 15,
    }
  }

  enable() {
    this.multiSelect.enable()
  }

  disable() {
    this.multiSelect.disable()
  }

  clear() {
    this.multiSelect.removeActiveItems()
  }

  selectAll() {
    // INTERNAL API: choiceList.element.children is undocumented.
    // Verify this still works when upgrading choices.js.
    // See also: choices.js watch entry in .dependency-constraints.yml
    const choices = this.multiSelect.choiceList.element.children
    const values = Array.from(choices, (element) =>
      element.getAttribute('data-value'),
    )

    // Update the selected choices
    this.multiSelect.setChoiceByValue(values)
  }
}
